import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import './LayoutComponents.css';
import { FaGlobe, FaSignOutAlt, FaUser, FaChevronDown, FaCog } from 'react-icons/fa';
import { logout } from '../../store/slices/authSlice';
import timesheetLogo from '../../assets/images/timesheet-logo.png';
import UserAvatar from '../UserAvatar';
import { useTranslation } from 'react-i18next';
import { setLanguage, setOptions } from '../../store/slices/languageSlice';

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { t, i18n } = useTranslation();
  const languageState = useSelector((state) => state.language || {}); 
  const currentLang = languageState.selected || i18n.language || 'en';
  const langOptions = languageState.options || []; 

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // ensure redux and i18n stay in sync
  useEffect(() => {
    if (currentLang && i18n.language !== currentLang) {
      i18n.changeLanguage(currentLang);
    }
  }, [currentLang, i18n]);

  // initialize language options in redux if not present (persisted across logins)
  useEffect(() => {
    if (!Array.isArray(langOptions) || langOptions.length === 0) {
      const defaultCodes = ['en', 'zh'];
      const opts = defaultCodes.map((code) => ({
        code,
        label: i18n.t(`languages.${code}`) || (code === 'en' ? 'English' : '中文'),
      }));
      dispatch(setOptions(opts));
    }
    // update labels when language changes (keeps labels localized)
  }, [dispatch, langOptions, i18n.language, i18n]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (code) => {
    if (!code) return;
    dispatch(setLanguage(code));
    i18n.changeLanguage(code);
  };

  return (
    <nav className="navbar-container login-navbar-container">
      <div className="navbar-left">
        {/* <span className="navbar-app-name">EZ Timesheet</span> */}
      </div>
      <div className="navbar-right">
        <div className="navbar-profile-dropdown" ref={dropdownRef}>
          <button className="navbar-profile-button" onClick={toggleDropdown}>
            <UserAvatar avatar={user?.avatar} className="navbar-profile-picture" size={40} />
            <FaChevronDown className={`dropdown-arrow ${isDropdownOpen ? 'rotated' : ''}`} />
          </button>
          {isDropdownOpen && (
            <div className="navbar-dropdown-menu">
              <div className="dropdown-profile-info">
                <UserAvatar avatar={user?.avatar} className="dropdown-profile-picture" size={48} />
                <div className="dropdown-user-info">
                  <div className="dropdown-username">{user?.userName || user?.username || 'User'}</div>
                  <div className="dropdown-jobtitle">{user?.title?.titleName || user?.role || ''}</div>
                </div>
              </div>
              <div className="dropdown-divider"></div>
              <Link to="/profile" className="dropdown-link" onClick={() => setIsDropdownOpen(false)}>
                <FaUser />
                {t('navbar.profile')}
              </Link>
              <Link to="/settings" className="dropdown-link" onClick={() => setIsDropdownOpen(false)}>
                <FaCog />
                {t('navbar.settings')}
              </Link>
              <button className="dropdown-link logout-link" onClick={handleLogout}>
                <FaSignOutAlt />
                {t('navbar.logout')}
              </button>
            </div>
          )}
        </div>
        <div className="navbar-language-wrapper">
          <FaGlobe />
          <select
            className="navbar-language-select"
            value={currentLang}
            onChange={(e) => handleLanguageChange(e.target.value)}
          >
            {langOptions.length > 0 ? langOptions.map((o) => (
              <option key={o.code} value={o.code}>
                {o.label}
              </option>
            )) : (
              <>
                <option value="en">English</option>
                <option value="zh">中文</option>
              </>
            )}
          </select>
        </div>
      </div>
    </nav>
  );
};

// LoginNavbar
export const LoginNavbar = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const languageState = useSelector((state) => state.language || {}); 
  const currentLang = languageState.selected || i18n.language || 'en';
  const langOptions = languageState.options || [];

  // ensure redux and i18n stay in sync
  useEffect(() => {
    if (currentLang && i18n.language !== currentLang) {
      i18n.changeLanguage(currentLang);
    }
  }, [currentLang, i18n]);

  // initialize language options in redux if not present (persisted across logins)
  useEffect(() => {
    if (!Array.isArray(langOptions) || langOptions.length === 0) {
      const defaultCodes = ['en', 'zh'];
      const opts = defaultCodes.map((code) => ({
        code,
        label: i18n.t(`languages.${code}`) || (code === 'en' ? 'English' : '中文'),
      }));
      dispatch(setOptions(opts));
    }
    // update labels when language changes (keeps labels localized)
  }, [dispatch, langOptions, i18n.language, i18n]);

  const handleLanguageChange = (code) => {
    if (!code) return;
    dispatch(setLanguage(code));
    i18n.changeLanguage(code);
  };

  return (
    <nav className="navbar-container login-navbar-container">
      <div className="navbar-left">
        <img src={timesheetLogo} alt="EZ Timesheet Logo" className="navbar-logo-image" />
        <span className="navbar-app-name">EZ Timesheet</span>
      </div>
      <div className="navbar-center">
        <Link to="/home" className="navbar-link">Home</Link>
        <Link to="/about" className="navbar-link">About Us</Link>
        <Link to="/contact" className="navbar-link">Contact Us</Link>
      </div>
      <div className="navbar-right">
        <div className="navbar-language-wrapper">
          <FaGlobe />
          <select
            className="navbar-language-select"
            value={currentLang}
            onChange={(e) => handleLanguageChange(e.target.value)}
          >
            {langOptions.length > 0 ? langOptions.map((o) => (
              <option key={o.code} value={o.code}>
                {o.label}
              </option>
            )) : (
              <>
                <option value="en">English</option>
                <option value="zh">中文</option>
              </>
            )}
          </select>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 